import { Calendar, XClose } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { useCurrentUserEntity } from '@/hooks/useCurrentUserEntity';
import { cancelBookingAsHost } from '@/lib/api/scheduling';
import type { ListPageConfig } from '@/lib/crm/core/types';
import { buildBookingListConfig, CRMObjectListPage } from '@/lib/crm/list';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

export default function BookingsPage() {
  const { currentOrg } = useOrg();
  const { entityId: currentUserEntityId } = useCurrentUserEntity();
  const navigate = useNavigate();
  useViewHealthMarker('bookings-list-ready', { ready: true });

  const handleCancel = useCallback(
    async (obj: CrmObject) => {
      if (!currentOrg) {
        console.warn('handleCancel: currentOrg is null');
        toast.error('Cannot cancel: no active organization');
        return;
      }
      try {
        await cancelBookingAsHost(currentOrg.id, obj.entity_id);
        toast.success('Booking cancelled');
      } catch (err) {
        logger.error(
          'Failed to cancel booking',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'BookingsPage', entityId: obj.entity_id }
        );
        toast.error("We couldn't cancel this booking. Try again.");
      }
    },
    [currentOrg]
  );

  const handleViewInCalendar = useCallback(
    (obj: CrmObject) => {
      if (!currentOrg) {
        console.warn('handleViewInCalendar: currentOrg is null');
        toast.error('Cannot open calendar: no active organization');
        return;
      }
      const startTime = getPropertyValue<string>(
        obj.properties?.ahoy_start_time
      );
      if (!startTime) {
        toast.error('Booking has no start time');
        return;
      }
      const date = startTime.slice(0, 10);
      navigate(`/${currentOrg.id}/meetings?date=${date}`);
    },
    [currentOrg, navigate]
  );

  // Append a per-user "host = me" condition to the static
  // `defaultFilterConditions` (which already includes "upcoming"). Both
  // render as visible removable chips on the filter bar — clearing them
  // shows past or other-host bookings respectively. Same UX shape as the
  // booking-links list page.
  const configWithActions: ListPageConfig = useMemo(() => {
    const base = buildBookingListConfig();
    const baseConditions = base.defaultFilterConditions ?? [];

    const conditions = currentUserEntityId
      ? [
          ...baseConditions,
          {
            id: 'default-host-me',
            field: 'ahoy_booking_to_host',
            // Frontend OperatorType — translated to backend `'exact'` by
            // convertConditionGroupsToFilterGroups when useListState wraps
            // conditions into a single group at apply time.
            operator: 'equals' as const,
            value: currentUserEntityId,
            association: 'ahoy_booking_to_host',
          },
        ]
      : baseConditions;

    return {
      ...base,
      defaultFilterConditions: conditions,
      rowActions: [
        {
          id: 'cancel',
          label: 'Cancel',
          icon: XClose,
          variant: 'danger',
          onClick: handleCancel,
          isDisabled: (obj: CrmObject) =>
            getPropertyValue<string>(obj.properties?.ahoy_status) ===
            'cancelled',
        },
        {
          id: 'view-in-calendar',
          label: 'View in calendar',
          icon: Calendar,
          onClick: handleViewInCalendar,
        },
      ],
    };
  }, [handleCancel, handleViewInCalendar, currentUserEntityId]);

  return <CRMObjectListPage config={configWithActions} />;
}
